import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail, Save, Loader2, Image as ImageIcon, RotateCcw, Send,
  Eye, Copy, Check, Info, AlertTriangle,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

/* ── Types ──────────────────────────────────────────────── */
type Token = { token: string; label: string; sample: string };

type TemplateResponse = {
  headerHtml: string;
  footerHtml: string;
  updatedAt: string | null;
  isDefault: boolean;
  defaults: { headerHtml: string; footerHtml: string };
  tokens: Token[];
};

type Field = 'header' | 'footer';

/* ── Image upload (presigned S3) ────────────────────────── */
async function uploadImage(file: File): Promise<string> {
  const { uploadUrl, publicUrl } = await api<{ uploadUrl: string; publicUrl: string }>(
    '/api/admin/email-template/image-upload-url',
    { method: 'POST', body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size }) },
  );
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type, 'Cache-Control': 'public, max-age=31536000' },
    body: file,
  });
  if (!put.ok) throw new Error('upload failed');
  return publicUrl;
}

export function AdminEmailTemplates() {
  const qc = useQueryClient();

  const [headerHtml, setHeaderHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [loaded, setLoaded]         = useState<{ header: string; footer: string } | null>(null);

  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const [testEmail, setTestEmail]   = useState('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [uploading, setUploading]   = useState(false);
  const [banner, setBanner]         = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const headerRef = useRef<HTMLTextAreaElement>(null);
  const footerRef = useRef<HTMLTextAreaElement>(null);
  const lastField = useRef<Field>('header');
  const fileInput = useRef<HTMLInputElement>(null);

  /* ── Load current template ── */
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'email-template'],
    queryFn: () => api<TemplateResponse>('/api/admin/email-template'),
  });

  useEffect(() => {
    if (!data) return;
    setHeaderHtml(data.headerHtml);
    setFooterHtml(data.footerHtml);
    setLoaded({ header: data.headerHtml, footer: data.footerHtml });
  }, [data]);

  const dirty = loaded !== null && (loaded.header !== headerHtml || loaded.footer !== footerHtml);

  /* ── Debounced live preview ── */
  const refreshPreview = useCallback(async (header: string, footer: string) => {
    setPreviewLoading(true);
    try {
      const { html } = await api<{ html: string }>('/api/admin/email-template/preview', {
        method: 'POST',
        body: JSON.stringify({ headerHtml: header, footerHtml: footer }),
      });
      setPreviewHtml(html);
    } catch {
      /* keep last good preview */
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loaded === null) return;
    const t = setTimeout(() => void refreshPreview(headerHtml, footerHtml), 500);
    return () => clearTimeout(t);
  }, [headerHtml, footerHtml, loaded, refreshPreview]);

  /* ── Save ── */
  const saveMutation = useMutation({
    mutationFn: () =>
      api<TemplateResponse>('/api/admin/email-template', {
        method: 'PUT',
        body: JSON.stringify({ headerHtml, footerHtml }),
      }),
    onSuccess: (res) => {
      setLoaded({ header: res.headerHtml, footer: res.footerHtml });
      qc.setQueryData(['admin', 'email-template'], (old: TemplateResponse | undefined) =>
        old ? { ...old, ...res } : old,
      );
      setBanner({ kind: 'ok', text: 'Plantilla guardada. Se aplicará a todos los emails.' });
    },
    onError: (err: unknown) => {
      setBanner({ kind: 'err', text: err instanceof ApiError ? err.message : 'No se pudo guardar.' });
    },
  });

  /* ── Send test ── */
  const testMutation = useMutation({
    mutationFn: () =>
      api<{ ok: true }>('/api/admin/email-template/test', {
        method: 'POST',
        body: JSON.stringify({ headerHtml, footerHtml, to: testEmail.trim() }),
      }),
    onSuccess: () => setBanner({ kind: 'ok', text: `Email de prueba enviado a ${testEmail.trim()}.` }),
    onError: (err: unknown) =>
      setBanner({ kind: 'err', text: err instanceof ApiError ? err.message : 'No se pudo enviar la prueba.' }),
  });

  /* ── Helpers ── */
  function copyToken(token: string) {
    void navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((c) => (c === token ? null : c)), 1500);
  }

  function insertAtCursor(snippet: string) {
    const field = lastField.current;
    const ref = field === 'header' ? headerRef : footerRef;
    const setter = field === 'header' ? setHeaderHtml : setFooterHtml;
    const el = ref.current;
    const current = field === 'header' ? headerHtml : footerHtml;
    if (!el) {
      setter(current + snippet);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function handleFile(file: File) {
    setBanner(null);
    setUploading(true);
    try {
      const url = await uploadImage(file);
      insertAtCursor(
        `\n<img src="${url}" alt="" width="520" style="display:block;max-width:100%;height:auto;border-radius:8px;" />\n`,
      );
      setBanner({ kind: 'ok', text: 'Imagen subida e insertada en el cursor.' });
    } catch (err) {
      setBanner({ kind: 'err', text: err instanceof ApiError ? err.message : 'No se pudo subir la imagen.' });
    } finally {
      setUploading(false);
    }
  }

  function restoreDefault(field: Field) {
    if (!data) return;
    if (field === 'header') setHeaderHtml(data.defaults.headerHtml);
    else setFooterHtml(data.defaults.footerHtml);
  }

  const tokens = data?.tokens ?? [];

  return (
    <div className="space-y-6">
      {/* hidden image input */}
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />

      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail size={22} className="text-brand-600" /> Plantillas de email
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Cabecera y pie que envuelven todos los emails transaccionales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              Cambios sin guardar
            </span>
          )}
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || saveMutation.isPending || isLoading}
            className="btn-primary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            Guardar cambios
          </button>
        </div>
      </div>

      {/* info */}
      <div className="flex gap-2.5 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        <Info size={16} className="text-brand-500 shrink-0 mt-0.5" />
        <p>
          Solo se editan la <strong>cabecera</strong> y el <strong>pie</strong>. El texto central de cada email
          (recuperación de contraseña, bienvenida, avisos…) no se modifica aquí. Usa HTML compatible con email
          (tablas y estilos en línea).
        </p>
      </div>

      {/* banner */}
      {banner && (
        <p
          className={cn(
            'text-sm rounded-xl px-3.5 py-2.5 flex items-center gap-2',
            banner.kind === 'ok'
              ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
              : 'text-red-700 bg-red-50 border border-red-200',
          )}
        >
          {banner.kind === 'ok' ? <Check size={15} /> : <AlertTriangle size={15} />}
          {banner.text}
        </p>
      )}

      {isLoading ? (
        <div className="p-12 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Cargando plantilla…
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* ── Editors ── */}
          <div className="space-y-5">
            {/* toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="btn-secondary gap-2 text-sm disabled:opacity-50"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                Subir imagen
              </button>
              <span className="text-xs text-slate-400">Se inserta donde tengas el cursor</span>
            </div>

            {/* tokens */}
            {tokens.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-3.5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
                  Variables disponibles · clic para copiar
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tokens.map((t) => (
                    <button
                      key={t.token}
                      onClick={() => copyToken(t.token)}
                      title={`${t.label} — ej: ${t.sample}`}
                      className="inline-flex items-center gap-1 text-xs font-mono bg-slate-100 hover:bg-brand-50 hover:text-brand-700 text-slate-600 rounded-md px-2 py-1 transition"
                    >
                      {copiedToken === t.token ? <Check size={11} /> : <Copy size={11} />}
                      {t.token}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <HtmlEditor
              label="Cabecera (header)"
              value={headerHtml}
              onChange={setHeaderHtml}
              textareaRef={headerRef}
              onFocus={() => (lastField.current = 'header')}
              onRestore={() => restoreDefault('header')}
            />
            <HtmlEditor
              label="Pie (footer)"
              value={footerHtml}
              onChange={setFooterHtml}
              textareaRef={footerRef}
              onFocus={() => (lastField.current = 'footer')}
              onRestore={() => restoreDefault('footer')}
            />

            {/* test email */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Send size={14} className="text-brand-600" /> Enviar email de prueba
              </div>
              <p className="text-xs text-slate-500">
                Envía un email de muestra con la plantilla actual (sin necesidad de guardar).
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className="input flex-1"
                />
                <button
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending || !/^\S+@\S+\.\S+$/.test(testEmail.trim())}
                  className="btn-secondary gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Enviar
                </button>
              </div>
            </div>
          </div>

          {/* ── Live preview ── */}
          <div className="lg:sticky lg:top-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Eye size={14} className="text-brand-600" /> Vista previa
                </span>
                {previewLoading && <Loader2 size={14} className="animate-spin text-slate-400" />}
              </div>
              <iframe
                title="Vista previa del email"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full h-[640px] bg-slate-100"
              />
            </div>
            <p className="mt-2 text-xs text-slate-400 text-center">
              El cuerpo central es solo un ejemplo para ver cómo encajan cabecera y pie.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── HTML editor block ──────────────────────────────────── */
function HtmlEditor(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onFocus: () => void;
  onRestore: () => void;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <span className="text-sm font-semibold text-slate-700">{props.label}</span>
        <button
          onClick={props.onRestore}
          className="text-xs text-slate-500 hover:text-brand-600 inline-flex items-center gap-1 transition"
        >
          <RotateCcw size={12} /> Restaurar por defecto
        </button>
      </div>
      <textarea
        ref={props.textareaRef}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        onFocus={props.onFocus}
        spellCheck={false}
        rows={12}
        className="w-full px-4 py-3 font-mono text-xs leading-relaxed text-slate-700 resize-y outline-none focus:bg-slate-50/50"
      />
    </div>
  );
}
